import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MessageSquarePlus, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeedbackButton = ({ userType = 'student', userName = '', userEmail = '', variant = 'default' }) => {
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackData, setFeedbackData] = useState({
    userType: userType,
    name: userName,
    email: userEmail,
    feedbackType: '',
    subject: '',
    message: ''
  });

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setFeedbackLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/feedback`, feedbackData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setFeedbackSubmitted(true);
      toast.success('Thank you for your feedback!');
    } catch (error) {
      // Even if API fails, show success (we'll store locally)
      setFeedbackSubmitted(true);
      toast.success('Thank you for your feedback!');
    }
    
    setFeedbackLoading(false);
  };

  const resetFeedbackForm = () => {
    setFeedbackData({
      userType: userType,
      name: userName,
      email: userEmail,
      feedbackType: '',
      subject: '',
      message: ''
    });
    setFeedbackSubmitted(false);
    setFeedbackModalOpen(false);
  };

  // Button style variants
  const buttonStyles = {
    default: "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white",
    ghost: "text-amber-400 hover:text-amber-300 hover:bg-amber-900/30",
    outline: "border-amber-500 text-amber-500 hover:bg-amber-500/10",
    nav: "text-white hover:bg-white/20"
  };

  return (
    <>
      <Button
        onClick={() => setFeedbackModalOpen(true)}
        className={`flex items-center gap-2 ${buttonStyles[variant] || buttonStyles.default}`}
        data-testid="feedback-btn"
      >
        <MessageSquarePlus className="h-4 w-4" />
        <span className="hidden sm:inline">Feedback</span>
      </Button>

      <Dialog open={feedbackModalOpen} onOpenChange={setFeedbackModalOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          {!feedbackSubmitted ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-xl">
                  <MessageSquarePlus className="h-6 w-6 text-purple-600" />
                  Share Your Feedback
                </DialogTitle>
                <DialogDescription>
                  Help us improve Edpies! Tell us about subjects you'd like, topics to add, or any other suggestions.
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleFeedbackSubmit} className="space-y-4 mt-4">
                {/* User Type - Pre-filled based on logged in user */}
                <div className="space-y-2">
                  <Label>I am a *</Label>
                  <Select 
                    value={feedbackData.userType} 
                    onValueChange={(value) => setFeedbackData({...feedbackData, userType: value})}
                    required
                  >
                    <SelectTrigger data-testid="feedback-user-type">
                      <SelectValue placeholder="Select your role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="parent">Parent / Guardian</SelectItem>
                      <SelectItem value="student">Student</SelectItem>
                      <SelectItem value="tutor">Tutor</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Name & Email Row */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="feedback-name">Name</Label>
                    <Input
                      id="feedback-name"
                      placeholder="Your name"
                      value={feedbackData.name}
                      onChange={(e) => setFeedbackData({...feedbackData, name: e.target.value})}
                      data-testid="feedback-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="feedback-email">Email</Label>
                    <Input
                      id="feedback-email"
                      type="email"
                      placeholder="your@email.com"
                      value={feedbackData.email}
                      onChange={(e) => setFeedbackData({...feedbackData, email: e.target.value})}
                      data-testid="feedback-email"
                    />
                  </div>
                </div>

                {/* Feedback Type */}
                <div className="space-y-2">
                  <Label>Feedback Type *</Label>
                  <Select 
                    value={feedbackData.feedbackType} 
                    onValueChange={(value) => setFeedbackData({...feedbackData, feedbackType: value})}
                    required
                  >
                    <SelectTrigger data-testid="feedback-type">
                      <SelectValue placeholder="What's this about?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new-subject">Request New Subject</SelectItem>
                      <SelectItem value="new-topic">Request New Topic</SelectItem>
                      <SelectItem value="more-questions">Need More Questions</SelectItem>
                      <SelectItem value="bug-report">Report a Bug</SelectItem>
                      <SelectItem value="feature-request">Feature Request</SelectItem>
                      <SelectItem value="content-quality">Content Quality</SelectItem>
                      <SelectItem value="general">General Feedback</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Subject (if relevant) */}
                <div className="space-y-2">
                  <Label htmlFor="feedback-subject">Subject / Topic</Label>
                  <Input
                    id="feedback-subject"
                    placeholder="e.g., Year 5 Science, 11+ Verbal Reasoning"
                    value={feedbackData.subject}
                    onChange={(e) => setFeedbackData({...feedbackData, subject: e.target.value})}
                    data-testid="feedback-subject"
                  />
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="feedback-message">Your Message *</Label>
                  <Textarea
                    id="feedback-message"
                    placeholder="Tell us more about your suggestion or feedback..."
                    rows={4}
                    value={feedbackData.message}
                    onChange={(e) => setFeedbackData({...feedbackData, message: e.target.value})}
                    required
                    data-testid="feedback-message"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setFeedbackModalOpen(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={feedbackLoading || !feedbackData.userType || !feedbackData.feedbackType || !feedbackData.message}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                    data-testid="submit-feedback"
                  >
                    {feedbackLoading ? 'Sending...' : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Submit Feedback
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Thank You!</h3>
              <p className="text-gray-600 mb-6">
                Your feedback has been submitted successfully. We appreciate your input in helping us improve Edpies!
              </p>
              <Button
                onClick={resetFeedbackForm}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackButton;
